import { useState } from "react";
import SntDialog from "@/components/SntDialog/SntDialog";
import SntCheckBox from "@cleanup/components/SntCheckBox/SntCheckBox";
import SntInfoText from "@cleanup/components/SntInfo/SntInfoText";
import { translate } from "@/hooks/useLanguage";
import { LanguageKey } from "@/types/generated/LanguageKey";

interface DeleteDialogProps {
  onDelete: (object: unknown, deleteAll: boolean) => void;
  title?: string;
  saveTxt?: string;
  erroTitle?: string;
  allTitle?: string;
}

interface UseDeleteAllDialogReturn {
  handleOpen: (object: unknown, content?: string) => void;
  handleClose: () => void;
  handleOpenError: (content?: string) => void;
  handleCloseError: () => void;
  DeleteDialog: () => React.JSX.Element;
}

/**
 * Custom hook for managing delete and error dialogs.
 * @param {object} props - The hook properties.
 * @param {function} props.onDelete - The callback function to handle the delete action.
 * @param {string} [props.title] - The custom title for the delete dialog.
 * @param {string} [props.erroTitle] - The custom title for the error dialog.
 * @returns {object} - The hook functions and components.
 */
const useDeleteAllDialog = ({
  onDelete,
  title,
  saveTxt,
  erroTitle,
  allTitle,
}: DeleteDialogProps): UseDeleteAllDialogReturn => {
  // State for delete dialog
  const [deleteObject, setDeleteObject] = useState<unknown>(null);
  const [deleteContent, setDeleteContent] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [deleteAll, setDeleteAll] = useState(false);

  // State for error dialog
  const [errorContent, setErrorContent] = useState("");
  const [isOpenError, setIsOpenError] = useState(false);

  // Function to open the delete dialog
  const handleOpen = (object: unknown, content?: string) => {
    setDeleteContent(content || translate(LanguageKey.delete_message));
    setDeleteObject(object);
    setIsOpen(true);
  };

  // Function to close the delete dialog
  const handleClose = () => {
    setIsOpen(false);
  };

  // Function to handle the delete action
  const handleDelete = () => {
    onDelete(deleteObject, deleteAll);
  };

  // Function to open the error dialog
  const handleOpenError = (content?: string) => {
    handleClose();
    setErrorContent(content || translate(LanguageKey.error_key));
    setIsOpenError(true);
  };

  // Function to close the error dialog
  const handleCloseError = () => {
    setIsOpenError(false);
  };

  // DeleteDialog component
  const DeleteDialogInner = () => {
    return (
      <SntDialog
        isShow={isOpen}
        onSave={handleDelete}
        onClose={handleClose}
        saveTxt={saveTxt || translate(LanguageKey.delete_key)}
        btnSaveClassName="danger"
        title={title || translate(LanguageKey.delete_key)}
      >
        {() => (
          <>
            <div>{deleteContent}</div>
            <div className="col-xs-12" style={{ display: "flex" }}>
              <SntCheckBox
                checked={deleteAll}
                label={allTitle}
                onChange={(e: boolean) => setDeleteAll(e)}
              />
            </div>
            <div className="col-xs-12">
              <SntInfoText>{translate(LanguageKey.mismatch_delete_info)}</SntInfoText>
            </div>
          </>
        )}
      </SntDialog>
    );
  };

  // ErrorDialog component
  const ErrorDialog = () => {
    return (
      <SntDialog
        isShow={isOpenError}
        onSave={handleCloseError}
        onClose={handleCloseError}
        saveTxt={translate(LanguageKey.ok_key)}
        btnSaveClassName="danger"
        isShowCloseButton={false}
        title={erroTitle || translate(LanguageKey.error_key)}
      >
        {() => <div>{errorContent}</div>}
      </SntDialog>
    );
  };

  // Combined component that renders both delete and error dialogs
  const DeleteDialogComponent = () => (
    <>
      <DeleteDialogInner />
      <ErrorDialog />
    </>
  );

  // Return the functions and components as an object
  return {
    handleOpen, // Function to open the delete dialog
    handleClose, // Function to close the delete dialog
    handleOpenError, // Function to open the error dialog
    handleCloseError, // Function to close the error dialog
    DeleteDialog: DeleteDialogComponent, // Component that renders both delete and error dialogs
  };
};

export default useDeleteAllDialog;
